/**
 * @file: ChatComposer.tsx
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * WeChat-style composer: a left voice-message button, the text in the
 * middle, and a right "+" button that toggles the {@link AttachmentPanel}.
 * When the input has text the "+" becomes a send button. On a phone the
 * middle is the app's voice composer (hold to talk and send the words, tap
 * to type, its mic dictates); on the web a growable text input. The screen
 * owns send/upload/record behavior via callbacks.
 */

import {
  ActiveMention,
  detectMentionAnchor,
  firstDiffIndex,
  mentionBlocks,
  snapSelectionOutOfBlocks,
} from '@euler/app/flows/chat/utils/mentions';
import { VoiceRecordingBar } from '@euler/components/VoiceRecordingBar';
import { FontFamily } from '@euler/components/typography';
import { DictationComposer } from '@euler/components/voice-input/DictationComposer';
import { VoiceComposerHandle } from '@euler/components/voice-input/composer/VoiceComposer';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ArrowUp, Check, Mic, Plus, X } from 'lucide-react-native';
import {
  FC,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  NativeSyntheticEvent,
  Platform,
  StyleSheet,
  TextInput,
  TextInputSelectionChangeEventData,
  TouchableOpacity,
  View,
} from 'react-native';
import { BehaviorSubject } from 'rxjs';
import { AttachmentAction, AttachmentPanel } from './AttachmentPanel';

/** Imperative handle the screen drives to insert a chosen `@mention`. */
export interface ChatComposerHandle {
  /** Insert `<label> ` at the active `@` anchor (or the caret) + refocus. */
  insertMention: (label: string) => void;
  focus: () => void;
}

interface ChatComposerProps {
  text: string;
  onChangeText: (v: string) => void;
  onSendText: () => void;
  sending: boolean;
  /** Left button — record + send a voice message. */
  onPressVoice: () => void;
  /** Words held and let go of on 发送 (or sent from the editor), phone only. */
  onSendSpoken: (words: string) => void;
  onSelectAttachment: (action: AttachmentAction) => void;
  /** True while a voice message / dictation is recording (mobile only). */
  recording: boolean;
  /** Live 0..1 mic level driving the inline waveform while recording. */
  meter$: BehaviorSubject<number>;
  onCancelVoice: () => void;
  onCompleteVoice: () => void;
  /** Group rooms only — detect `@` and open the member picker. */
  mentionEnabled?: boolean;
  /** Fired when a fresh `@` trigger is typed (screen opens the picker). */
  onMentionTrigger?: () => void;
  /** Active mention chips — drive atomic-tag selection + delete behavior. */
  mentions?: ActiveMention[];
}

export const ChatComposer = memo(
  forwardRef<ChatComposerHandle, ChatComposerProps>(function ChatComposer(
    {
      text,
      onChangeText,
      onSendText,
      sending,
      onPressVoice,
      onSendSpoken,
      onSelectAttachment,
      recording,
      meter$,
      onCancelVoice,
      onCompleteVoice,
      mentionEnabled,
      onMentionTrigger,
      mentions,
    },
    ref,
  ) {
    const { rpx } = useResponsiveContext();
    const inputRef = useRef<TextInput>(null);
    // A phone's input is the voice composer's, while its box shows.
    const voiceRef = useRef<VoiceComposerHandle>(null);
    const [dictating, setDictating] = useState(false);
    const currentInput = useCallback(
      () =>
        Platform.OS === 'web'
          ? inputRef.current
          : voiceRef.current?.input() ?? null,
      [],
    );
    // Refs so the change/selection handlers read the latest text + chips
    // without re-creating the memoized callbacks on every keystroke.
    const textRef = useRef(text);
    textRef.current = text;
    const mentionsRef = useRef<ActiveMention[]>(mentions ?? []);
    mentionsRef.current = mentions ?? [];
    const isWeb = Platform.OS === 'web';
    const baseHeightRef = useRef(0);
    const minH = isWeb ? 40 : rpx(40);
    const maxH = isWeb ? 120 : rpx(120);
    const [inputHeight, setInputHeight] = useState(minH);
    const [panelOpen, setPanelOpen] = useState(false);
    // Caret tracking, the active `@` anchor, and a pending caret to restore
    // AFTER a chip insert commits. Selection is driven IMPERATIVELY
    // (`setSelection` on the input ref), NOT via the controlled `selection`
    // prop — the controlled prop fights the value update on device (the
    // picker modal also steals first-responder), which was dropping the
    // inserted chip and resetting the caret.
    const selectionRef = useRef<{ start: number; end: number }>({
      start: 0,
      end: 0,
    });
    const mentionAnchorRef = useRef<number | null>(null);
    // Caret position to restore after a programmatic text change commits.
    // The backspace block-delete applies it immediately (input still
    // focused); a chip insert leaves the input blurred (the picker modal
    // had focus) and the caret is applied by `focus()`, which the screen
    // calls once the picker has fully closed.
    const pendingCaretRef = useRef<number | null>(null);
    // Re-entrancy guard: our own imperative snap re-fires onSelectionChange.
    const applyingSelectionRef = useRef(false);

    const setNativeSelection = useCallback(
      (start: number, end: number) => {
        const input = currentInput() as
          | (TextInput & { setSelection?: (s: number, e: number) => void })
          | null;
        input?.setSelection?.(start, end);
      },
      [currentInput],
    );

    useImperativeHandle(
      ref,
      () => ({
        focus: () =>
          Platform.OS === 'web'
            ? inputRef.current?.focus()
            : voiceRef.current?.focus(),
        insertMention: (label: string) => {
          const anchor = mentionAnchorRef.current;
          // Replace exactly the trigger `@` with the chip token + trailing
          // space (the search query lives in the picker, never the
          // composer, so there's nothing else to consume). Fall back to a
          // caret insert if the anchor no longer points at an `@`.
          const validAnchor = anchor != null && text[anchor] === '@';
          const start = validAnchor ? anchor! : selectionRef.current.start;
          const end = validAnchor ? anchor! + 1 : start;
          const insert = label + ' ';
          const next = text.slice(0, start) + insert + text.slice(end);
          const pos = start + insert.length;
          mentionAnchorRef.current = null;
          onChangeText(next);
          // Single, reliable re-focus once the picker modal has surely
          // dismissed. iOS rejects focus while the modal is still
          // present/tearing down, so DON'T attempt it earlier — a premature
          // focus leaves a half-focused state that then blocks this one
          // (the source of intermittent "not focused"). The screen holds
          // the keyboard inset until `keyboardWillShow`, so no reflow.
          setTimeout(() => {
            if (Platform.OS === 'web') inputRef.current?.focus();
            else voiceRef.current?.focus();
            setNativeSelection(pos, pos);
            selectionRef.current = { start: pos, end: pos };
          }, 320);
        },
      }),
      [text, onChangeText, setNativeSelection],
    );

    // Backspace block-delete keeps focus, so place the caret on the next
    // frame once the shortened text commits. (A chip insert leaves the
    // input blurred — its caret is handled by `focus()`.)
    useEffect(() => {
      const pos = pendingCaretRef.current;
      if (pos == null || !currentInput()?.isFocused?.()) return;
      pendingCaretRef.current = null;
      selectionRef.current = { start: pos, end: pos };
      const raf = requestAnimationFrame(() => setNativeSelection(pos, pos));
      return () => cancelAnimationFrame(raf);
    }, [text, setNativeSelection, currentInput]);

    const onSelectionChange = useCallback(
      (e: NativeSyntheticEvent<TextInputSelectionChangeEventData>) => {
        const sel = e.nativeEvent.selection;
        selectionRef.current = sel;
        if (!mentionEnabled || applyingSelectionRef.current) return;
        // Keep the caret/selection out of the middle of a mention tag: a
        // tap snaps to the nearer edge, a range expands to cover the tag.
        const blocks = mentionBlocks(textRef.current, mentionsRef.current);
        if (blocks.length === 0) return;
        const snapped = snapSelectionOutOfBlocks(sel, blocks);
        if (snapped.start !== sel.start || snapped.end !== sel.end) {
          applyingSelectionRef.current = true;
          selectionRef.current = snapped;
          setNativeSelection(snapped.start, snapped.end);
          setTimeout(() => {
            applyingSelectionRef.current = false;
          }, 0);
        }
      },
      [mentionEnabled, setNativeSelection],
    );

    useEffect(() => {
      if (!isWeb || !inputRef.current) return;
      const el = inputRef.current as unknown as HTMLTextAreaElement;
      baseHeightRef.current = el.scrollHeight;
      setInputHeight(baseHeightRef.current);
    }, [isWeb]);

    const handleChangeText = useCallback(
      (val: string) => {
        const prev = text;
        let nextVal = val;
        if (mentionEnabled) {
          // A freshly-typed `@` at a word boundary opens the member picker.
          const anchor = detectMentionAnchor(prev, val);
          if (anchor != null) {
            mentionAnchorRef.current = anchor;
            // WeChat: the keyboard hides + input blurs as the picker takes
            // over the bottom; re-focus happens on selection (insertMention).
            currentInput()?.blur();
            onMentionTrigger?.();
          } else if (val.length === prev.length - 1) {
            // Single-char delete: if it fell inside an atomic mention block
            // (the tag or its trailing space), drop the WHOLE block — you
            // can't shave a tag char-by-char (WeChat behavior).
            const delIdx = firstDiffIndex(prev, val);
            const block = mentionBlocks(prev, mentionsRef.current).find(
              b => delIdx >= b.start && delIdx < b.end,
            );
            if (block) {
              nextVal = prev.slice(0, block.start) + prev.slice(block.end);
              pendingCaretRef.current = block.start;
            }
          }
        }
        onChangeText(nextVal);
        if (!isWeb || !inputRef.current) return;
        const el = inputRef.current as unknown as HTMLTextAreaElement;
        const base = baseHeightRef.current;
        el.style.height = base + 'px';
        const h =
          el.scrollHeight > base ? Math.min(el.scrollHeight, maxH) : base;
        el.style.height = h + 'px';
        setInputHeight(h);
      },
      [
        text,
        isWeb,
        maxH,
        onChangeText,
        mentionEnabled,
        onMentionTrigger,
        currentInput,
      ],
    );

    const handleSubmit = useCallback(() => {
      if (!text.trim() || sending) return;
      onSendText();
      const base = baseHeightRef.current || minH;
      setInputHeight(base);
      if (isWeb && inputRef.current) {
        (inputRef.current as unknown as HTMLTextAreaElement).style.height =
          base + 'px';
      }
    }, [text, sending, onSendText, minH, isWeb]);

    useEffect(() => {
      if (!isWeb || !inputRef.current) return;
      const el = inputRef.current as unknown as HTMLTextAreaElement;
      const handler = (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          handleSubmit();
        }
      };
      el.addEventListener('keydown', handler);
      return () => el.removeEventListener('keydown', handler);
    }, [isWeb, handleSubmit]);

    const togglePanel = useCallback(() => {
      setPanelOpen(o => !o);
      if (isWeb && inputRef.current) inputRef.current.blur();
    }, [isWeb]);

    const handleSelect = useCallback(
      (action: AttachmentAction) => {
        setPanelOpen(false);
        onSelectAttachment(action);
      },
      [onSelectAttachment],
    );

    const typed = text.trim().length > 0 && !sending;
    // Still dictating: send shows but waits for the words to settle.
    const canSend = typed && !dictating;
    const btnSize = isWeb ? baseHeightRef.current || 40 : rpx(40);

    const rowStyle = {
      flexDirection: 'row' as const,
      paddingHorizontal: rpx(10),
      paddingVertical: rpx(10),
      gap: rpx(8),
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: '#e5e7eb',
    };

    // While recording (mobile only) the input is replaced by an inline
    // waveform; the flanking buttons become cancel / complete.
    if (recording) {
      return (
        <View style={{ backgroundColor: '#fafafa' }}>
          <View style={[rowStyle, { alignItems: 'center' }]}>
            <RoundButton size={btnSize} onPress={onCancelVoice}>
              <X color="#475569" size={rpx(20)} strokeWidth={2.5} />
            </RoundButton>
            <VoiceRecordingBar meter$={meter$} />
            <RoundButton
              size={btnSize}
              onPress={onCompleteVoice}
              backgroundColor="#3b82f6"
            >
              <Check color="#fff" size={rpx(20)} strokeWidth={3} />
            </RoundButton>
          </View>
        </View>
      );
    }

    return (
      <View style={{ backgroundColor: '#fafafa' }}>
        <View style={[rowStyle, { alignItems: 'flex-end' }]}>
          {/* Voice messages work on web too — the browser records webm and
              the media service transcodes it to mp3 so it plays on every
              client. Dictation is the phone's voice composer's, below. */}
          <RoundButton size={btnSize} onPress={onPressVoice}>
            <Mic color="#475569" size={rpx(20)} />
          </RoundButton>

          {isWeb ? (
            <View
              style={{
                flex: 1,
                flexDirection: 'row',
                alignItems: 'flex-end',
                backgroundColor: '#F1F5F9',
                borderRadius: rpx(22),
                borderWidth: 1,
                borderColor: '#E2E8F0',
                paddingRight: rpx(8),
              }}
            >
              <TextInput
                ref={inputRef}
                value={text}
                onChangeText={handleChangeText}
                onSelectionChange={onSelectionChange}
                placeholder="输入消息..."
                placeholderTextColor="#94A3B8"
                multiline
                maxLength={2000}
                onFocus={() => setPanelOpen(false)}
                style={[
                  {
                    flex: 1,
                    fontSize: rpx(15),
                    fontFamily: FontFamily.NotoSans.Regular,
                    color: '#1E293B',
                    paddingHorizontal: rpx(16),
                    paddingTop: rpx(10),
                    paddingBottom: rpx(10),
                    height: Math.max(minH, Math.min(inputHeight, maxH)),
                  },
                  { resize: 'none', overflow: 'hidden' } as any,
                ]}
                blurOnSubmit={false}
              />
            </View>
          ) : (
            <DictationComposer
              ref={voiceRef}
              value={text}
              onChangeText={handleChangeText}
              onSend={(words, from) => {
                if (from === 'box') handleSubmit();
                else onSendSpoken(words);
              }}
              placeholder="输入消息..."
              onDictatingChange={setDictating}
              inputProps={{
                onSelectionChange,
                maxLength: 2000,
                onFocus: () => setPanelOpen(false),
              }}
            />
          )}

          {typed ? (
            <RoundButton
              size={btnSize}
              onPress={handleSubmit}
              disabled={!canSend}
              backgroundColor="#3b82f6"
            >
              <ArrowUp color="#fff" size={rpx(20)} strokeWidth={2.5} />
            </RoundButton>
          ) : (
            <RoundButton size={btnSize} onPress={togglePanel}>
              <Plus color="#475569" size={rpx(22)} />
            </RoundButton>
          )}
        </View>

        {panelOpen ? <AttachmentPanel onSelect={handleSelect} /> : null}
      </View>
    );
  }),
);

const RoundButton: FC<{
  size: number;
  onPress: () => void;
  disabled?: boolean;
  backgroundColor?: string;
  children: React.ReactNode;
}> = ({ size, onPress, disabled, backgroundColor = '#E2E8F0', children }) => (
  <TouchableOpacity
    activeOpacity={0.7}
    onPress={onPress}
    disabled={disabled}
    style={{
      width: size,
      height: size,
      borderRadius: size / 2,
      backgroundColor,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 2,
      opacity: disabled ? 0.45 : 1,
    }}
  >
    {children}
  </TouchableOpacity>
);
